import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { ApiError, api } from './api';

export type User = {
  id: string;
  email: string;
  name: string | null;
  pictureUrl: string | null;
};

type AuthContextValue = {
  user: User | null;
  loading: boolean;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Rehydrate the session once on mount: the cookie is httpOnly, so asking the
  // server is the only way to know whether we are signed in. Signing *in* is a
  // full-page redirect through Google, so there is no login call to make here.
  useEffect(() => {
    const controller = new AbortController();

    api
      .get<{ user: User }>('/api/auth/me', controller.signal)
      .then(({ user: me }) => setUser(me))
      .catch((error: unknown) => {
        // A 401 here just means "not signed in" -- not an error worth surfacing.
        if (!(error instanceof ApiError) && !controller.signal.aborted) {
          console.error('Failed to restore session', error);
        }
        setUser(null);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, []);

  const logout = useCallback(async () => {
    await api.post('/api/auth/logout');
    setUser(null);
  }, []);

  const value = useMemo(() => ({ user, loading, logout }), [user, loading, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
